import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Ban, Check, X, ChevronDown, ChevronUp, PauseCircle, UserCheck, Repeat, Flag } from "lucide-react";
import api from "@/lib/api";
import { LIVE_SCENARIOS } from "@/lib/liveOps";
import { timeAgo } from "@/lib/constants";

const TRACE_IDS = ["ftd", "kyc", "compliance", "suppressed", "retention", "withdrawal-pt"];

const OVERRIDE_CONTROLS = [
  { key: "pause", label: "Pause Workflow", icon: PauseCircle },
  { key: "escalate", label: "Escalate to Human", icon: UserCheck },
  { key: "reassign", label: "Reassign", icon: Repeat },
  { key: "review", label: "Mark for Review", icon: Flag },
];

export default function ComplianceView() {
  const [open, setOpen] = useState("compliance");
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/governance").then((r) => setAudit((r.data.audit || []).slice(0, 8))).catch(() => {});
  }, []);

  const traces = LIVE_SCENARIOS.filter((s) => TRACE_IDS.includes(s.id));

  const override = (label) => toast.info(`${label} — simulated control`, {
    description: "Broker retains control. No production effect in this demonstration environment.",
  });

  return (
    <div className="space-y-6" data-testid="compliance-view">
      <div>
        <div className="text-[10px] font-mono tracking-widest text-emerald-500 uppercase mb-1">Compliance View · Simulated demo data</div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Governance &amp; Decision Control
        </h1>
        <p className="text-sm text-zinc-400 mt-1">Why every action was allowed, blocked or escalated — and where humans retain control.</p>
      </div>

      <div className="border border-red-500/30 bg-red-500/5 rounded-md p-5" data-testid="withdrawal-boundary-note">
        <div className="text-[10px] font-mono tracking-widest text-red-400 uppercase mb-2">Withdrawal Non-Interference — permanent boundary</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div>
            <div className="text-zinc-400 mb-1.5 font-semibold">O.N.E.Tech does not:</div>
            <div className="flex flex-wrap gap-1.5">
              {["Approve", "Reject", "Delay", "Block", "Modify", "Influence"].map((w) => (
                <span key={w} className="px-2 py-0.5 rounded-full text-[10px] font-mono border border-red-500/40 bg-red-500/10 text-red-400">{w}</span>
              ))}
              <span className="text-zinc-500 self-center">withdrawal decisions.</span>
            </div>
          </div>
          <div>
            <div className="text-zinc-400 mb-1.5 font-semibold">Permitted functions only:</div>
            <div className="space-y-1">
              {["Approved status communication", "Human support routing", "Operational monitoring", "Audit logging"].map((w) => (
                <div key={w} className="flex items-center gap-1.5 text-zinc-300"><Check className="w-3 h-3 text-emerald-400" /> {w}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="human-override-controls">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-zinc-200">Human Override — the broker retains control</div>
            <p className="text-[11px] text-zinc-500 mt-0.5">Authorised brokerage users can intervene in any workflow at any time. Simulated controls.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {OVERRIDE_CONTROLS.map((c) => (
              <button key={c.key} data-testid={`override-${c.key}`} onClick={() => override(c.label)}
                className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200">
                <c.icon className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} /> {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="decision-traces">
        <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Decision traces — click to inspect</div>
        {traces.map((s) => {
          const blocked = s.policy.blocked;
          const isOpen = open === s.id;
          return (
            <div key={s.id} className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid={`trace-${s.id}`}>
              <button
                data-testid={`trace-toggle-${s.id}`}
                onClick={() => setOpen(isOpen ? null : s.id)}
                className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-zinc-800/40 transition-colors duration-150"
              >
                <div className="flex items-center gap-3 text-left">
                  <span className="text-xs font-semibold text-zinc-100">{s.client.name}</span>
                  <span className="text-[11px] text-zinc-500">{s.event.type}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${blocked ? "text-red-400 border-red-500/40 bg-red-500/10" : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"}`}>
                    {blocked ? "AUTOMATION BLOCKED" : "AUTOMATION PERMITTED"}
                  </span>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 fade-slide-in" data-testid={`trace-detail-${s.id}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="space-y-2 text-xs">
                      <div><span className="text-zinc-600">Client: </span><span className="text-zinc-200">{s.client.name} ({s.client.ref})</span></div>
                      <div><span className="text-zinc-600">Operational event: </span><span className="text-zinc-200">{s.event.type} — {s.event.source}</span></div>
                      <div><span className="text-zinc-600">Requested workflow: </span><span className="text-zinc-200">{s.decision.workflow}</span></div>
                      <div><span className="text-zinc-600">Decision: </span>
                        <span className={`font-semibold ${blocked ? "text-red-400" : "text-emerald-400"}`}>
                          {blocked ? "AUTOMATION BLOCKED" : "AUTOMATION PERMITTED"} — {s.decision.type}
                        </span>
                      </div>
                      {s.policy.blockReason && <div><span className="text-zinc-600">Reason: </span><span className="text-zinc-300">{s.policy.blockReason}</span></div>}
                      <div><span className="text-zinc-600">Executed: </span><span className="text-zinc-300">
                        {s.action.kind === "automated" ? `${s.action.language} ${s.action.channel} — template ${s.action.template}` : s.action.kind === "human" ? `CRM task → ${s.action.task.assignee}` : "No communication"}
                      </span></div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-2">Policy checks</div>
                      <div className="space-y-1.5">
                        {s.policy.checks.map((c, i) => {
                          const failed = c.includes("FAILED");
                          return (
                            <div key={i} className="flex items-start gap-2 text-[11px]">
                              {failed ? <X className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" /> : <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />}
                              <span className={failed ? "text-red-400" : "text-zinc-400"}>{c.replace(" — FAILED", "")}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="compliance-audit">
        <div className="flex items-center gap-2 mb-3">
          <Ban className="hidden" />
          <span className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Audit history — latest entries</span>
        </div>
        <div className="space-y-2">
          {audit.length === 0 && <div className="text-xs text-zinc-600">No audit entries yet — run any workflow action on a client profile.</div>}
          {audit.map((a) => (
            <div key={a.id} className="flex items-center gap-3 text-[11px]">
              <span className="font-mono text-zinc-500 w-20 shrink-0">{timeAgo(a.ts)}</span>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 shrink-0">{a.action}</span>
              <span className="text-zinc-400 truncate">{a.client_name ? `${a.client_name} — ` : ""}{a.detail}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
